import React, { useEffect, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { useLanguage } from '@/language/LanguageContext';

export const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative h-[100dvh] w-[99dvw] overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1533134486753-c833f0ed4866?auto=format&fit=crop&w=2000&q=80"
          alt="Hero background"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-background/70" />
        {/* Gradient overlay from bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-24 lg:pb-32 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto w-full">
          {/* Animated content */}
          <div
            className={`transition-all duration-1000 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Subtitle */}
            <p className="text-muted-foreground text-sm md:text-base font-light tracking-[0.2em] uppercase mb-4 lg:mb-6">
              {t('hero.subtitle')}
            </p>

            {/* Main heading */}
            <h1 className="text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight leading-[1.1] max-w-4xl">
              {t('hero.title1')}
              <br />
              <span className="text-muted-foreground">{t('hero.title2')}</span>
            </h1>

            {/* Description */}
            <p
              className={`text-muted-foreground text-base md:text-lg font-light max-w-xl mt-6 lg:mt-8 leading-relaxed transition-all duration-1000 delay-200 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {t('hero.description')}
            </p>
          </div>

          {/* Scroll indicator */}
          <button
            onClick={scrollToAbout}
            className={`mt-12 lg:mt-16 flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group cursor-pointer ${
              isLoaded ? 'animate-fade-in' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.6s' }}
          >
            <span className="text-xs font-light tracking-[0.2em] uppercase">
              {t('hero.scroll')}
            </span>
            <ArrowDown
              size={16}
              className="animate-bounce group-hover:animate-none"
            />
          </button>
        </div>
      </div>

      {/* Side text decoration */}
      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 z-10">
        <p
          className={`text-muted-foreground/30 text-xs tracking-[0.3em] uppercase rotate-90 origin-center whitespace-nowrap transition-all duration-1000 delay-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {t('hero.sideText')}
        </p>
      </div>
    </section>
  );
};
